/**
 * cv-text.ts — Build a single plain-text representation of a CV for AI endpoints.
 *
 * Pure function — no React, no hooks. Takes the CV data and a translation
 * function so it can be used both inside React components (via useCallback)
 * and outside (tests, server-side, workers).
 *
 * Used by App.tsx → passed as `getResumeText` to:
 *   useAiAnalyses, useCvGeneration, useCoverLetter, useAutoIa, useCvTranslation
 */

import type { CVData } from '../types';
import type { getTType } from '../contexts/I18nContext';

/**
 * Serialise a CVData object to a plain-text string suitable for sending to an AI model.
 *
 * @param cv  - The CV data object.
 * @param t   - i18n translation function (only needed for section labels; falls back to French).
 */
export function cvDataToText(cv: CVData, t: getTType): string {
  const p = cv.personalInfo;
  const lines: string[] = [];

  // Identity
  const name = [p.civility, p.firstName, p.lastName].filter(Boolean).join(' ');
  if (name) lines.push(name);
  if (p.title) lines.push(p.title);
  if (p.email) lines.push(p.email);
  if (p.phone) lines.push(p.phone);
  if (p.location || p.city || p.country) {
    lines.push([p.city, p.country].filter(Boolean).join(', ') || p.location || '');
  }
  if (p.summary) lines.push('\n' + t('sections.profil') + '\n' + p.summary);

  // Experience
  if (cv.experience?.length) {
    lines.push('\n' + t('sections.experiences'));
    cv.experience.forEach((e) => {
      lines.push(`${e.position} - ${e.company} (${e.startDate} - ${e.endDate})`);
      if (e.description) lines.push(e.description);
    });
  }

  // Education
  if (cv.education?.length) {
    lines.push('\n' + t('sections.formation'));
    cv.education.forEach((e) => {
      lines.push(`${e.degree} - ${e.school} (${e.date || e.endDate || ''})`);
    });
  }

  // Skills / languages / hobbies
  if (cv.skills?.length) lines.push('\n' + t('skills.label') + '\n' + cv.skills.join(', '));
  if (cv.languages?.length) lines.push('\n' + t('languagesLabel') + '\n' + cv.languages.join(', '));
  if (cv.hobbies?.length) lines.push('\n' + t('hobbies.label') + '\n' + cv.hobbies.join(', '));

  return lines.join('\n');
}
